import ModalV2 from "@/js/components/ModalV2/ModalV2";
import { __ } from "@/utils/Trans";
import adminStore from "@/js/modules/admin/store/AdminStore";
import { useQuery } from "@tanstack/react-query";

interface IUserDetailModal {
	open: boolean;
	onClose: () => void;
	onSuccess: () => void;
	user: any
}

export default function UserDetailModal({ onClose, onSuccess, open, user }: IUserDetailModal) {

	const { data: userData } = useQuery({
		queryKey: ['userDetail', user.email],
		queryFn: async () => {
			const result = await adminStore.fetchUserLogs(user.email);
			return result;
		},
	});

	const { data: userPaymentData } = useQuery({
		queryKey: ['userPayment', user.id],
		queryFn: async () => {
			const result = await adminStore.fetchPaymentDetails(user.id);
			return result;
		},
	});

	const { data: userSubscriptionStatus } = useQuery({
		queryKey: ['userSubsStatus', user.id],
		queryFn: async () => {
			const result = await adminStore.fetchSubscriptionStatus(user.id);
			return result;
		},
	});


	return (
		<>
			<ModalV2
				open={open}
				onClose={onClose}
			>
				{!userData || !userPaymentData || !userSubscriptionStatus ? <div className="min-h-screen flex items-center justify-center">
					<div className="flex flex-col items-center">
						<div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-blue-500"></div>
						<p className="mt-4 text-gray-700 text-lg">Veriler yükleniyor...</p>
					</div>
				</div> :
					<div className="min-h-screen w-full bg-white px-8 rounded-lg shadow-xl space-y-6">
						<div>
							<h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
								Kullanıcı Profili
							</h2>
						</div>

						{/* Static User Information */}
						<div className="border-b border-gray-200 pb-4">
							<h3 className="text-lg leading-6 font-medium text-gray-900 mb-3">Kişisel Bilgiler</h3>
							<div className="grid grid-cols-1 gap-y-3">
								<div className="flex items-center">
									<strong className="w-1/3 text-gray-700">Ad:</strong>
									<span className="w-2/3 text-gray-900">{user.firstName}</span>
								</div>
								<div className="flex items-center">
									<strong className="w-1/3 text-gray-700">Soyad:</strong>
									<span className="w-2/3 text-gray-900">{user.lastName}</span>
								</div>
								<div className="flex items-center">
									<strong className="w-1/3 text-gray-700">Email:</strong>
									<span className="w-2/3 text-gray-900">{user.email}</span>
								</div>
							</div>
						</div>

						{/* Dynamic Data */}
						<div>
							<h3 className="text-lg leading-6 font-medium text-gray-900 mb-3">Hesap Detayları</h3>
							<div className="grid grid-cols-1 gap-y-3">
								<div className="flex items-center">
									<strong className="w-1/2 text-gray-700">Email Doğrulama Tarihi:</strong>
									<span className="w-1/2 text-gray-900">{adminStore.getDateByFormat(userData.emailVerifiedAt)}</span>
								</div>
								<div className="flex items-center">
									<strong className="w-1/2 text-gray-700">Son Giriş Tarihi:</strong>
									<span className="w-1/2 text-gray-900">{adminStore.getDateByFormat(userData.lastLoginDate)}</span>
								</div>
								<div className="flex items-center">
									<strong className="w-1/2 text-gray-700">Blok Adı:</strong>
									<span className="w-1/2 text-gray-900">{userData.blok_name || "N/A"}</span>
								</div>
								<div className="flex items-center">
									<strong className="w-1/2 text-gray-700">Blok Oluşturulma Tarihi:</strong>
									<span className="w-1/2 text-gray-900">{adminStore.getDateByFormat(userData.blok_created)}</span>
								</div>
								<div className="flex items-center">
									<strong className="w-1/2 text-gray-700">Şirket Oluşturulma Tarihi:</strong>
									<span className="w-1/2 text-gray-900">{userData.company_created ? adminStore.getDateByFormat(userData.company_created) : "N/A"}</span>
								</div>
								<div className="flex items-center">
									<strong className="w-1/2 text-gray-700">Şirket Adı:</strong>
									<span className="w-1/2 text-gray-900">{userData.title || "N/A"}</span>
								</div>
								<div className="flex items-center">
									<strong className="w-1/2 text-gray-700">Profil Güncelleme Tarihi:</strong>
									<span className="w-1/2 text-gray-900">{adminStore.getDateByFormat(userData.profile_updated)}</span>
								</div>
							</div>
						</div>

						{/* Subscription Status Data */}
						<div className="max-w-md w-full bg-white p-8 rounded-lg shadow-xl space-y-6 mt-8 mx-auto">
							<div>
								<h2 className="text-center text-3xl font-extrabold text-gray-900 mb-6">
									Abonelik Durumu
								</h2>
								<div className="grid grid-cols-1 gap-y-3">
									<div className="flex items-center">
										<strong className="w-1/2 text-gray-700">Plan Adı:</strong>
										<span className="w-1/2 text-gray-900">{userSubscriptionStatus.plan?.title || userSubscriptionStatus.plan?.name || "N/A"}</span>
									</div>
									<div className="flex items-center">
										<strong className="w-1/2 text-gray-700">Durum:</strong>
										<span className={`w-1/2 font-semibold ${userSubscriptionStatus.status === 'active' ? 'text-green-600' : 'text-red-600'}`}>
											{userSubscriptionStatus.status ? (userSubscriptionStatus.status === 'active' ? 'Aktif' : 'Pasif') : "N/A"}
										</span>
									</div>
									<div className="flex items-center">
										<strong className="w-1/2 text-gray-700">Başlangıç Tarihi:</strong>
										<span className="w-1/2 text-gray-900">{adminStore.getDateByFormat(userSubscriptionStatus.startsAt)}</span>
									</div>
									{userSubscriptionStatus.endsAt && (
										<div className="flex items-center">
											<strong className="w-1/2 text-gray-700">Bitiş Tarihi:</strong>
											<span className="w-1/2 text-gray-900">{adminStore.getDateByFormat(userSubscriptionStatus.endsAt)}</span>
										</div>
									)}
									<div className="flex items-center">
										<strong className="w-1/2 text-gray-700">Fiyat:</strong>
										<span className="w-1/2 text-gray-900">
											{userSubscriptionStatus.plan?.price !== undefined && userSubscriptionStatus.plan?.price !== null
												? `${userSubscriptionStatus.plan.price} ${userSubscriptionStatus.plan.currency}`
												: "N/A"}
										</span>
									</div>
									<div className="flex items-center">
										<strong className="w-1/2 text-gray-700">Üye Sayısı:</strong>
										<span className="w-1/2 text-gray-900">{userSubscriptionStatus.plan?.memberCount || "N/A"}</span>
									</div>
									{userSubscriptionStatus.pendingPlan && ( // Display if there's a pending plan
										<div className="mt-4 border-t border-gray-200 pt-4">
											<h4 className="text-md font-medium text-gray-800 mb-2">Bekleyen Plan</h4>
											<p className="text-sm text-gray-600">
												<strong className="text-gray-700">Adı:</strong> {userSubscriptionStatus.pendingPlan.title || userSubscriptionStatus.pendingPlan.name || "N/A"}
											</p>
											<p className="text-sm text-gray-600">
												<strong className="text-gray-700">Başlangıç Tarihi:</strong> {adminStore.getDateByFormat(userSubscriptionStatus.pendingPlanStartsAt)}
											</p>
										</div>
									)}
								</div>
							</div>
						</div>

						{/* Payment History Data */}
						<div className="max-w-3xl w-full bg-white p-8 rounded-lg shadow-xl space-y-6 mt-8">
							<div>
								<h2 className="text-center text-3xl font-extrabold text-gray-900 mb-6">
									Ödeme Geçmişi
								</h2>
								{userPaymentData?.length === 0 ? (
									<p className="text-center text-gray-600">Henüz bir ödeme kaydınız bulunmamaktadır.</p>
								) : (
									<div className="space-y-6">
										{userPaymentData?.map((payment, index) => (
											<div key={payment.id || index} className="border border-gray-200 rounded-lg p-6 bg-gray-50 shadow-sm">
												<h3 className="text-xl font-semibold text-gray-800 mb-3">
													{payment.package?.name || "Paket Adı Yok"}
												</h3>
												<p className="text-sm text-gray-600 mb-2">
													<strong className="text-gray-700">Sahip:</strong> {payment.ownerName || "N/A"}
												</p>
												<p className="text-sm text-gray-600 mb-2">
													<strong className="text-gray-700">Oluşturulma Tarihi:</strong> {adminStore.getDateByFormat(payment.created)}
												</p>
												<p className="text-sm text-gray-600 mb-2">
													<strong className="text-gray-700">Fiyat:</strong> {payment.price} {payment.currency}
												</p>

												{/* Invoice Details */}
												{payment.invoice && (
													<div className="mt-4 border-t border-gray-200 pt-4">
														<h4 className="text-md font-medium text-gray-800 mb-2">Fatura Bilgileri</h4>
														<p className="text-sm text-gray-600">
															<strong className="text-gray-700">Ad Soyad:</strong> {payment.invoice.fullName || "N/A"}
														</p>
														<p className="text-sm text-gray-600">
															<strong className="text-gray-700">Email:</strong> {payment.invoice.email || "N/A"}
														</p>
														<p className="text-sm text-gray-600">
															<strong className="text-gray-700">Adres:</strong> {payment.invoice.address || "N/A"}
														</p>
														<p className="text-sm text-gray-600">
															<strong className="text-gray-700">Telefon:</strong> {payment.invoice.gsmNumber || "N/A"}
														</p>
													</div>
												)}

												{/* Package Description (HTML content) */}
												{payment.package?.description && (
													<div className="mt-4 border-t border-gray-200 pt-4">
														<h4 className="text-md font-medium text-gray-800 mb-2">Paket Açıklaması</h4>
														<div
															className="prose prose-sm max-w-none text-gray-700"
															dangerouslySetInnerHTML={adminStore.getLimitedHtmlContent(payment.package.description)}
														></div>
													</div>
												)}

												{/* Package Image (If available) */}
												{payment.package?.imageUrl && (
													<div className="mt-4 border-t border-gray-200 pt-4">
														<h4 className="text-md font-medium text-gray-800 mb-2">Paket Görseli</h4>
														<img
															src={payment.package.imageUrl}
															alt={payment.package.name || "Paket Görseli"}
															className="w-full h-48 object-cover rounded-md mt-2"
														/>
													</div>
												)}
											</div>
										))}
									</div>
								)}
							</div>
						</div>

					</div>
				}
			</ModalV2>
		</>

	)
}
